import styled from '@emotion/styled'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

type Props = {
  children: React.ReactNode
  isExpanded: boolean
  collapsedHeight: number | 'content'
  id?: string
  transition?: string
  containerStyles?: React.CSSProperties
}

const ExpandableDiv = ({
  children,
  collapsedHeight,
  isExpanded,
  id,
  transition,
  containerStyles
}: Props) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const [renderedHeight, setRenderedHeight] = useState<number>(0)
  const [animationReady, setAnimationReady] = useState(false)

  // Pre-measure height before animating
  useLayoutEffect(() => {
    const el = contentRef.current
    if (!el) return

    // Measure height first
    const nextHeight = el.scrollHeight

    if (isExpanded || collapsedHeight === 'content') {
      setRenderedHeight(nextHeight)
    } else if (typeof collapsedHeight === 'number') {
      setRenderedHeight(collapsedHeight)
    }

    // Enable animation on next paint
    requestAnimationFrame(() => {
      setAnimationReady(true)
    })
  }, [isExpanded, collapsedHeight])

  // Listen for dynamic content changes after animation begins
  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    const resizeObserver = new ResizeObserver(() => {
      const nextHeight = el.scrollHeight
      if (isExpanded || collapsedHeight === 'content') {
        setRenderedHeight(nextHeight)
      }
    })

    resizeObserver.observe(el)
    return () => resizeObserver.disconnect()
  }, [isExpanded, collapsedHeight])

  return (
    <ExpandableDivContainer
      id={id}
      transition={animationReady ? transition : 'none'}
      style={containerStyles}
    >
      <ContentContainer
        height={renderedHeight}
        transition={animationReady ? transition : 'none'}
      >
        <div ref={contentRef}>{children}</div>
      </ContentContainer>
    </ExpandableDivContainer>
  )
}

const ExpandableDivContainer = styled.div<{
  transition?: string
}>(({ transition = 'height ease 500ms' }) => ({
  overflow: 'hidden',
  transition: transition
}))

const ContentContainer = styled.div<{
  height: number
  transition?: string
}>(({ height, transition = 'height ease 500ms' }) => ({
  height: height,
  transition: transition
}))

export default ExpandableDiv
